import React, { useState, useEffect, useCallback } from "react";
import { Service, SubService } from "../../../types";
import { subServicesApi } from "../../../services/subServicesApi";
import { servicesApi } from "../../../services/servicesApi";
import {
  Modal,
  ActionButton,
  EmptyState,
  Card,
  Loading,
  ErrorState,
} from "../../shared";
import { Icons } from "../../shared/Icons";

interface ServiceSubServicesModalProps {
  isOpen: boolean;
  onClose: () => void;
  serviceId: number;
  onRefresh?: () => void;
}

export const ServiceSubServicesModal: React.FC<
  ServiceSubServicesModalProps
> = ({ isOpen, onClose, serviceId, onRefresh }) => {
  const [service, setService] = useState<Service | null>(null);
  const [allSubServices, setAllSubServices] = useState<SubService[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [linkPrices, setLinkPrices] = useState<Record<number, number>>({});
  const [priceInputs, setPriceInputs] = useState<Record<number, string>>({});
  const [isRequiredMap, setIsRequiredMap] = useState<Record<number, boolean>>(
    {}
  );

  const fetchService = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await servicesApi.getServiceById(serviceId);
      if (response.success && response.data) {
        setService(response.data);
      } else {
        setError(response.error || "Falha ao carregar servi\u00e7o");
      }
    } catch (err) {
      setError("Falha ao carregar servi\u00e7o");
    } finally {
      setLoading(false);
    }
  }, [serviceId]);

  const initializePricesFromService = useCallback(() => {
    if (!service) return;

    const pricesMap: Record<number, number> = {};
    const priceInputsMap: Record<number, string> = {};
    const isRequiredMap: Record<number, boolean> = {};
    service.subServiceRelations.forEach((relation) => {
      pricesMap[relation.subServiceId] = parseFloat(relation.price);
      priceInputsMap[relation.subServiceId] = relation.price.toString();
      isRequiredMap[relation.subServiceId] = relation.isRequired || false;
    });
    setLinkPrices(pricesMap);
    setPriceInputs(priceInputsMap);
    setIsRequiredMap(isRequiredMap);
  }, [service]);

  const fetchAllSubServices = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const subServicesResponse = await subServicesApi.getSubServicesByCompany();

      if (subServicesResponse.success && subServicesResponse.data) {
        setAllSubServices(subServicesResponse.data);
      } else {
        setError(subServicesResponse.error || "Erro ao carregar sub-serviços");
      }
    } catch (err) {
      setError("Erro ao carregar dados");
      console.error("Error fetching data:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      fetchService();
    }
  }, [isOpen, serviceId, fetchService]);

  useEffect(() => {
    if (service) {
      fetchAllSubServices();
      initializePricesFromService();
    }
  }, [service, fetchAllSubServices, initializePricesFromService]);

  const formatCurrency = (price: number | string | null | undefined) => {
    const numericPrice = typeof price === "string" ? parseFloat(price) : price;
    const safePrice =
      typeof numericPrice === "number" && !isNaN(numericPrice)
        ? numericPrice
        : 0;
    return `R$ ${safePrice.toFixed(2).replace(".", ",")}`;
  };

  const getLinkedSubServices = () => {
    if (!service) return [];
    return service.subServiceRelations
      .filter((relation) => relation.isActive)
      .map((relation) => ({
        id: relation.id,
        serviceId: relation.serviceId,
        subServiceId: relation.subServiceId,
        price: relation.price,
        subService: relation.subService,
      }));
  };

  const getAvailableSubServices = () => {
    if (!service) return [];
    const linkedIds = service.subServiceRelations
      .filter((relation) => relation.isActive)
      .map((relation) => relation.subServiceId);
    return allSubServices.filter(
      (subService) => subService.isActive && !linkedIds.includes(subService.id)
    );
  };

  const handlePriceChange = (subServiceId: number, price: number) => {
    setLinkPrices((prev) => ({
      ...prev,
      [subServiceId]: price,
    }));
  };

  const handlePriceInputChange = (subServiceId: number, inputValue: string) => {
    setPriceInputs((prev) => ({
      ...prev,
      [subServiceId]: inputValue,
    }));

    // Update the numeric price if valid
    if (inputValue === "") {
      handlePriceChange(subServiceId, 0);
    } else {
      const numValue = parseFloat(inputValue);
      if (!isNaN(numValue)) {
        handlePriceChange(subServiceId, numValue);
      }
    }
  };

  const handleIsRequiredChange = (
    subServiceId: number,
    isRequired: boolean
  ) => {
    setIsRequiredMap((prev) => ({
      ...prev,
      [subServiceId]: isRequired,
    }));
  };

  const handleLinkSubService = async (subService: SubService) => {
    const price = linkPrices[subService.id] || 0;
    if (price < 0) {
      setError("Por favor, defina um preço válido para o sub-serviço");
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      const isRequired = isRequiredMap[subService.id] || false;
      const response = await servicesApi.createServiceSubServiceLink(
        service?.id || 0,
        subService.id,
        Number(price),
        isRequired
      );

      if (response.success && response.data) {
        // Refresh local service data
        await fetchService();
        // Notify parent to refresh service data
        onRefresh?.();
        // Reset the price input and isRequired checkbox
        setLinkPrices((prev) => {
          const newPrices = { ...prev };
          delete newPrices[subService.id];
          return newPrices;
        });
        setPriceInputs((prev) => {
          const newInputs = { ...prev };
          delete newInputs[subService.id];
          return newInputs;
        });
        setIsRequiredMap((prev) => {
          const newMap = { ...prev };
          delete newMap[subService.id];
          return newMap;
        });
      } else {
        setError(response.error || "Erro ao vincular sub-serviço");
      }
    } catch (err) {
      setError("Erro ao vincular sub-serviço");
      console.error("Error linking subservice:", err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleUnlinkSubService = async (subServiceId: number) => {
    setSubmitting(true);
    setError(null);
    try {
      if (!service) {
        setError("Serviço não encontrado");
        return;
      }
      const relation = service.subServiceRelations.find(
        (relation) => relation.subServiceId === subServiceId
      );
      if (!relation) {
        setError("Relação não encontrada");
        return;
      }

      const response = await servicesApi.deleteServiceSubServiceLink(
        relation.id
      );

      if (response.success) {
        // Refresh local service data
        await fetchService();
        // Notify parent to refresh service data
        onRefresh?.();
      } else {
        setError(response.error || "Erro ao desvincular sub-serviço");
      }
    } catch (err) {
      setError("Erro ao desvincular sub-serviço");
      console.error("Error unlinking subservice:", err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Gerenciar Sub-serviços${service ? ` - ${service.name}` : ""}`}
      size="lg"
    >
      <div className="space-y-6 overflow-x-hidden">
        {loading ? (
          <Loading message="Carregando serviço..." size="lg" className="py-8" />
        ) : !service ? (
          <ErrorState
            message="Erro ao carregar serviço"
            variant="minimal"
            icon={<Icons.Close sx={{ fontSize: 24 }} />}
            className="py-8"
          />
        ) : (
          <>
            {error && <ErrorState message={error} variant="inline" size="sm" />}

            {loading ? (
              <Loading
                message="Carregando sub-serviços..."
                size="lg"
                className="py-12"
              />
            ) : allSubServices.length === 0 ? (
              <EmptyState
                icon={<Icons.Build sx={{ fontSize: 80 }} />}
                title="Nenhum sub-serviço encontrado"
                description="Crie primeiro alguns sub-serviços para poder vinculá-los aos serviços."
              />
            ) : (
              <div className="space-y-6">
                {/* Info Box */}
                <div className="bg-blue-50 dark:bg-blue-950/40 border border-blue-200 dark:border-blue-800 rounded-xl p-4">
                  <div className="flex flex-col sm:flex-row sm:items-start gap-3">
                    <Icons.Lightbulb sx={{ fontSize: 24, color: "#f59e0b" }} className="shrink-0" />
                    <div className="min-w-0">
                      <p className="font-medium text-blue-900 dark:text-blue-200">
                        Gerenciar Sub-serviços
                      </p>
                      <p className="text-sm text-blue-700 dark:text-blue-300 mt-0.5">
                        Gerencie os sub-serviços vinculados ao serviço "
                        {service.name}". Você pode definir preços específicos
                        para cada sub-serviço.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Currently Linked Subservices */}
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <span className="text-lg">🔗</span>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      Sub-serviços Vinculados ({getLinkedSubServices().length})
                    </h3>
                  </div>

                  {getLinkedSubServices().length === 0 ? (
                    <div className="bg-gray-50 dark:bg-slate-900/50 border border-gray-200 dark:border-slate-600 rounded-xl p-6 text-center">
                      <div className="flex flex-col items-center gap-2">
                        <span className="text-3xl text-gray-400">🔗</span>
                        <p className="text-gray-600 font-medium">
                          Nenhum sub-serviço vinculado
                        </p>
                        <p className="text-sm text-gray-500">
                          Vincule sub-serviços da lista abaixo para adicionar
                          opções extras ao serviço.
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {getLinkedSubServices().map((linkedItem) => {
                        const { subService, price } = linkedItem;
                        if (!subService) return null;

                        return (
                          <Card
                            key={subService.id}
                            className="p-4 border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-950/40"
                          >
                            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                              <div className="flex-1 min-w-0">
                                <h4 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white truncate">
                                  {subService.name}
                                </h4>
                                <div className="flex items-center gap-2 mt-1">
                                  <span className="text-sm font-medium text-green-700 dark:text-green-300">
                                    Preço atual: {formatCurrency(price)}
                                  </span>
                                </div>
                              </div>

                              <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-end border-t border-green-200 dark:border-green-800 pt-4 sm:border-0 sm:pt-0 sm:ml-4">
                                <div className="flex flex-row flex-wrap items-center gap-3 sm:gap-4">
                                  <div className="flex flex-col">
                                    <label className="text-xs font-medium text-gray-500 mb-1">
                                      Novo preço:
                                    </label>
                                    <div className="flex items-center">
                                      <span className="text-sm text-gray-500 mr-1">
                                        R$
                                      </span>
                                      <input
                                        type="number"
                                        value={
                                          priceInputs[subService.id] !== undefined
                                            ? priceInputs[subService.id]
                                            : price.toString()
                                        }
                                        onChange={(e) => {
                                          handlePriceInputChange(
                                            subService.id,
                                            e.target.value
                                          );
                                        }}
                                        className="w-20 px-2 py-1.5 text-sm border border-gray-300 dark:border-slate-600 rounded bg-white dark:bg-slate-900 text-gray-900 dark:text-slate-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        min="0"
                                        step="1"
                                        placeholder="0,00"
                                      />
                                    </div>
                                  </div>

                                  <div className="flex flex-col">
                                    <label className="text-xs font-medium text-gray-500 mb-1">
                                      Obrigatório:
                                    </label>
                                    <input
                                      type="checkbox"
                                      checked={
                                        isRequiredMap[subService.id] !== undefined
                                          ? isRequiredMap[subService.id]
                                          : service.subServiceRelations.find(
                                              (r) =>
                                                r.subServiceId === subService.id
                                            )?.isRequired || false
                                      }
                                      onChange={(e) => {
                                        handleIsRequiredChange(
                                          subService.id,
                                          e.target.checked
                                        );
                                      }}
                                      disabled={submitting}
                                      className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                                    />
                                  </div>

                                  {((linkPrices[subService.id] !== undefined &&
                                    linkPrices[subService.id] !== Number(price)) ||
                                    (isRequiredMap[subService.id] !== undefined &&
                                      isRequiredMap[subService.id] !==
                                        (service.subServiceRelations.find(
                                          (r) =>
                                            r.subServiceId === subService.id
                                        )?.isRequired || false))) && (
                                    <ActionButton
                                      onClick={() => {
                                        const currentPrice =
                                          linkPrices[subService.id] !== undefined
                                            ? linkPrices[subService.id]
                                            : price;
                                        const currentIsRequired =
                                          isRequiredMap[subService.id] !==
                                          undefined
                                            ? isRequiredMap[subService.id]
                                            : service.subServiceRelations.find(
                                                (r) =>
                                                  r.subServiceId === subService.id
                                              )?.isRequired || false;

                                        const relation =
                                          service.subServiceRelations.find(
                                            (relation) =>
                                              relation.subServiceId ===
                                              subService.id
                                          );
                                        if (relation) {
                                          setSubmitting(true);
                                          servicesApi
                                            .updateServiceSubServiceLink(
                                              relation.id,
                                              Number(currentPrice),
                                              currentIsRequired
                                            )
                                            .then((response) => {
                                              if (response.success) {
                                                setLinkPrices((prev) => {
                                                  const newPrices = { ...prev };
                                                  delete newPrices[subService.id];
                                                  return newPrices;
                                                });
                                                setIsRequiredMap((prev) => {
                                                  const newRequired = { ...prev };
                                                  delete newRequired[
                                                    subService.id
                                                  ];
                                                  return newRequired;
                                                });
                                                onRefresh?.();
                                              } else {
                                                setError(
                                                  response.error ||
                                                    "Erro ao atualizar"
                                                );
                                              }
                                            })
                                            .catch(() => {
                                              setError("Erro ao atualizar");
                                            })
                                            .finally(() => {
                                              setSubmitting(false);
                                            });
                                        }
                                      }}
                                      variant="primary"
                                      size="sm"
                                      disabled={
                                        submitting ||
                                        (linkPrices[subService.id] || 0) < 0
                                      }
                                      className="w-full sm:min-w-[80px] sm:w-auto"
                                    >
                                      {submitting ? (
                                        <span className="flex items-center gap-1">
                                          <span className="animate-spin">⏳</span>
                                          <span className="text-xs">
                                            Salvando...
                                          </span>
                                        </span>
                                      ) : (
                                        <span className="flex items-center gap-1">
                                          <span>💾</span>
                                          <span className="text-xs">Salvar</span>
                                        </span>
                                      )}
                                    </ActionButton>
                                  )}

                                  <ActionButton
                                    onClick={() =>
                                      handleUnlinkSubService(subService.id)
                                    }
                                    variant="danger"
                                    disabled={submitting}
                                    size="sm"
                                    className="w-full sm:min-w-[100px] sm:w-auto"
                                  >
                                    {submitting ? (
                                      <span className="flex items-center gap-2">
                                        <Icons.Refresh sx={{ fontSize: 18 }} className="animate-spin" />
                                        Removendo...
                                      </span>
                                    ) : (
                                      <span className="flex items-center gap-2">
                                        <Icons.LinkOff sx={{ fontSize: 18 }} />
                                        Desvincular
                                      </span>
                                    )}
                                  </ActionButton>
                                </div>
                              </div>
                            </div>
                          </Card>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Available Subservices to Link */}
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <Icons.Add sx={{ fontSize: 22 }} />
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      Sub-serviços Disponíveis (
                      {getAvailableSubServices().length})
                    </h3>
                  </div>

                  {getAvailableSubServices().length === 0 ? (
                    <div className="bg-gray-50 dark:bg-slate-900/50 border border-gray-200 dark:border-slate-600 rounded-xl p-6 text-center">
                      <div className="flex flex-col items-center gap-2">
                        <Icons.Check sx={{ fontSize: 40 }} className="text-gray-400" />
                        <p className="text-gray-600 font-medium">
                          Todos os sub-serviços já estão vinculados
                        </p>
                        <p className="text-sm text-gray-500">
                          Todos os sub-serviços ativos desta empresa já estão
                          vinculados a este serviço.
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {getAvailableSubServices().map((subService) => {
                        const currentPrice = linkPrices[subService.id] || 0;

                        return (
                          <Card key={subService.id} className="p-4">
                            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                              <div className="flex-1 min-w-0">
                                <h4 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white truncate">
                                  {subService.name}
                                </h4>
                              </div>

                              <div className="flex flex-row flex-wrap items-center gap-3 border-t border-gray-200 dark:border-slate-600 pt-4 sm:border-0 sm:pt-0 sm:ml-4">
                                <div className="flex flex-col">
                                  <label className="text-xs font-medium text-gray-500 mb-1">
                                    Preço:
                                  </label>
                                  <div className="flex items-center">
                                    <span className="text-sm text-gray-500 mr-1">
                                      R$
                                    </span>
                                    <input
                                      type="number"
                                      value={priceInputs[subService.id] || ""}
                                      onChange={(e) => {
                                        handlePriceInputChange(
                                          subService.id,
                                          e.target.value
                                        );
                                      }}
                                      className="w-20 px-2 py-1.5 text-sm border border-gray-300 dark:border-slate-600 rounded bg-white dark:bg-slate-900 text-gray-900 dark:text-slate-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                      min="0"
                                      step="1"
                                      placeholder="0,00"
                                    />
                                  </div>
                                </div>

                                <div className="flex flex-col">
                                  <label className="text-xs font-medium text-gray-500 mb-1">
                                    Obrigatório:
                                  </label>
                                  <input
                                    type="checkbox"
                                    checked={
                                      isRequiredMap[subService.id] || false
                                    }
                                    onChange={(e) =>
                                      handleIsRequiredChange(
                                        subService.id,
                                        e.target.checked
                                      )
                                    }
                                    className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                                  />
                                </div>

                                <ActionButton
                                  onClick={() =>
                                    handleLinkSubService(subService)
                                  }
                                  variant="primary"
                                  disabled={submitting || currentPrice < 0}
                                  className="w-full sm:min-w-[100px] sm:w-auto"
                                  size="sm"
                                >
                                  {submitting ? (
                                    <span className="flex items-center gap-2">
                                      <span className="animate-spin">⏳</span>
                                      Vinculando...
                                    </span>
                                  ) : (
                                    <span className="flex items-center gap-2">
                                      🔗 Vincular
                                    </span>
                                  )}
                                </ActionButton>
                              </div>
                            </div>
                          </Card>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <div className="flex flex-col-reverse sm:flex-row justify-end gap-3 pt-4 sm:pt-6 border-t border-gray-200 dark:border-slate-700 mt-4">
        <ActionButton onClick={onClose} variant="outline" size="sm" className="w-full sm:w-auto">
          Fechar
        </ActionButton>
      </div>
    </Modal>
  );
};
